import { Injectable, signal } from '@angular/core';
import { ApiService } from './api.service';
import {
  CampaignForm,
  ChatMessage,
  ConversationThreadDetail,
  ConversationThreadSummary,
  defaultForm,
  Person,
  Reminder,
  ResolveAudienceResponse,
} from '../models/campaign.model';

// Backend writes this as the user message when a campaign is submitted straight from the form.
const DIRECT_SUBMIT_PLACEHOLDER = '[Campaign submitted directly from the form]';
const SEND_FAILED_STATUS = 'SendFailed';

/** Central shared state for the app — mirrors Streamlit's st.session_state. */
@Injectable({ providedIn: 'root' })
export class AppStateService {
  constructor(private api: ApiService) { }

  readonly form = signal<CampaignForm>(defaultForm());
  readonly messages = signal<ChatMessage[]>([]);
  readonly userPhoto = signal<string>('');
  readonly userInitials = signal<string>('');

  readonly teamsOptions = signal<string[]>([]);
  readonly locationsOptions = signal<string[]>([]);

  readonly peopleCandidatesInc = signal<Person[]>([]);
  readonly peopleCandidatesExc = signal<Person[]>([]);
  readonly peopleShownInc = signal<number>(10);
  readonly peopleShownExc = signal<number>(10);

  readonly peekResults = signal<ResolveAudienceResponse | null>(null);
  readonly conversations = signal<ConversationThreadSummary[]>([]);
  readonly activeThreadId = signal<string | null>(null);
  readonly activeConversationId = signal<string | null>(null);
  readonly activeMessageId = signal<number | null>(null);
  readonly initializing = signal<boolean>(false);

  private makeTitle(messages: ChatMessage[]): string {
    const firstUserMsg = messages.find((m) => m.role === 'user');
    if (firstUserMsg) {
      const summary = firstUserMsg.content.replace(/\s+/g, ' ').trim();
      if (!summary) {
        return 'Conversation';
      }
      const sentenceMatch = summary.match(/^.{1,90}?[.!?](?=\s|$)/);
      const candidate = sentenceMatch?.[0] || summary;
      return candidate.substring(0, 80) + (candidate.length > 80 ? '...' : '');
    }
    return 'Conversation';
  }

  private turnsToMessages(detail: ConversationThreadDetail): ChatMessage[] {
    type Entry = { at: number; messages: ChatMessage[] };

    const entries: Entry[] = detail.turns
      .filter((turn) => turn.user_message !== DIRECT_SUBMIT_PLACEHOLDER)
      .map((turn) => ({
        at: Date.parse(turn.created_at),
        messages: [
          { role: 'user' as const, content: turn.user_message },
          { role: 'assistant' as const, content: turn.assistant_reply },
        ],
      }));

    for (const version of detail.submitted_campaigns ?? []) {
      const failed = version.status === SEND_FAILED_STATUS;
      entries.push({
        at: Date.parse(version.created_at),
        messages: [
          {
            role: 'user' as const,
            content: this.buildApprovalSummary(
              version.submitted_form,
              version.audience_count,
              version.campaign_alias || version.campaign_id,
              version.version_number,
              failed,
            ),
          },
          {
            role: 'assistant' as const,
            content: failed
              ? 'This campaign could not be sent for approval. Please try sending it again.'
              : 'Campaign submitted for approval.',
          },
        ],
      });
    }

    return entries.sort((a, b) => a.at - b.at).flatMap((entry) => entry.messages);
  }

  buildApprovalSummary(
    form: any,
    audienceCount: number,
    campaignAlias: string,
    versionNumber: number,
    failed = false,
  ): string {
    const lines = [
      failed ? 'Campaign could not be sent for approval ⚠️' : 'Campaign sent for approval ✅',
      `Campaign ID: ${campaignAlias}`,
      `Version: ${versionNumber}`,
      `Name: ${form.campaign_name || '(untitled)'}`,
      `Priority: ${form.priority}`,
      `Channel: ${form.channel || '—'}`,
      `Recipients: ${audienceCount || 0}`,
      `Approver: ${form.approver || '—'}`,
    ];

    const reminders: Reminder[] = form.reminders || [];
    if (reminders.length) {
      lines.push(`Reminders: ${reminders.length}`);
      for (const reminder of reminders) {
        lines.push(`  ${reminder.reminder_number}. ${reminder.send_datetime.replace('T', ' ')}`);
      }
    }

    return lines.join('\n');
  }

  initializeConversationState(): void {
    if (this.initializing()) {
      return;
    }
    this.initializing.set(true);
    this.refreshConversationThreads(() => {
      this.initializing.set(false);
    });
  }

  refreshConversationThreads(onComplete?: () => void): void {
    this.api.listConversationThreads().subscribe({
      next: (threads) => {
        this.conversations.set(threads);
        onComplete?.();
      },
      error: () => {
        this.conversations.set([]);
        onComplete?.();
      },
    });
  }

  startNewConversation(): void {
    // Reset to a clean, unsaved conversation. The backend mints the thread on
    // the first chat turn, so we avoid persisting empty placeholder threads.
    this.messages.set([]);
    this.setForm(defaultForm());
    this.activeThreadId.set(null);
    this.activeConversationId.set(null);
    this.activeMessageId.set(null);
    this.refreshConversationThreads();
  }

  loadConversation(threadId: string): void {
    this.api.getConversationThread(threadId).subscribe({
      next: (detail) => {
        this.activeThreadId.set(detail.thread_id);
        this.activeConversationId.set(detail.turns.at(-1)?.conversation_id ?? null);
        this.activeMessageId.set(detail.latest_message_id);
        this.messages.set(this.turnsToMessages(detail));
        this.setForm(detail.current_form);
        this.refreshConversationThreads();
      },
      error: () => {
        this.messages.set([]);
        this.setForm(defaultForm());
      },
    });
  }

  deleteConversation(threadId: string): void {
    this.api.deleteConversationThread(threadId).subscribe({
      next: () => {
        const isActive = this.activeThreadId() === threadId;
        this.refreshConversationThreads(() => {
          if (isActive) {
            this.startNewConversation();
          }
        });
      },
      error: () => undefined,
    });
  }

  clearAllConversations(): void {
    this.api.clearConversationThreads().subscribe({
      next: () => {
        this.conversations.set([]);
        this.startNewConversation();
      },
      error: () => undefined,
    });
  }

  setForm(form: CampaignForm): void {
    this.form.set({
      ...form,
      audience: { ...form.audience, copilot_users_only: form.audience.copilot_users_only ?? true },
    });
  }

  mergePatch(patch: Partial<CampaignForm> & { audience?: any }): void {
    const current = { ...this.form(), audience: { ...this.form().audience } };
    for (const key of Object.keys(patch) as (keyof CampaignForm)[]) {
      if (key === 'audience' && patch.audience) {
        const akeys: (keyof CampaignForm['audience'])[] = [
          'locations',
          'teams',
          'copilot_users_only',
          'inclusions',
          'exclusions',
        ];
        for (const akey of akeys) {
          const val = (patch.audience as any)[akey];
          if (val !== undefined && val !== null) {
            (current.audience as any)[akey] = val;
          }
        }
      } else if (key in current) {
        (current as any)[key] = (patch as any)[key];
      }
    }
    this.form.set(current);
  }

  addMessage(msg: ChatMessage): void {
    this.messages.update((m) => [...m, msg]);
  }

  setConversationContext(
    threadId: string | null,
    messageId: number | null = null,
    conversationId: string | null = null,
  ): void {
    this.activeThreadId.set(threadId);
    this.activeMessageId.set(messageId);
    this.activeConversationId.set(conversationId);
  }

  currentConversationTitle(): string {
    const campaignName = (this.form().campaign_name || '').trim();
    if (campaignName) {
      return campaignName;
    }
    const threadId = this.activeThreadId();
    if (!threadId) {
      return this.makeTitle(this.messages());
    }
    return this.conversations().find((thread) => thread.thread_id === threadId)?.title
      ?? this.makeTitle(this.messages());
  }
}
